import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, Check } from "lucide-react";
import { AIButton, AIPanel, CopyButton, useAIAssist } from "@/components/AIAssist";

/** Lightweight creator-facing Content Assistant for an accepted collaboration. */
export function ContentAssistant({ collaborationId }) {
  const [aiEnabled, setAiEnabled] = useState(true);
  const [pending, setPending] = useState(null);
  const ai = useAIAssist("/ai/collaboration/assist");

  useEffect(() => {
    api.get("/ai/status").then((r) => setAiEnabled(!!r.data.enabled)).catch(() => {});
  }, []);

  const run = (action) => { setPending(action); ai.run({ collaboration_id: collaborationId, action }).finally(() => setPending(null)); };

  return (
    <div className="rounded-3xl border border-zinc-200 bg-white p-6" data-testid="content-assistant">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-emerald-600" />
        <h2 className="font-display text-lg font-bold text-zinc-900">Content Assistant</h2>
      </div>
      <p className="mt-1 text-sm text-zinc-500">Ideas for this collaboration — not mandatory wording. Keep your own voice and authenticity.</p>

      {!aiEnabled ? (
        <p className="mt-4 rounded-xl bg-zinc-50 px-3 py-2 text-sm text-zinc-500">AI Assist is temporarily unavailable.</p>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap gap-2">
            <AIButton testid="ca-content-ideas" loading={pending === "content_ideas"} label="Content ideas" onClick={() => run("content_ideas")} />
            <AIButton testid="ca-captions" loading={pending === "captions"} label="Caption ideas" onClick={() => run("captions")} />
            <AIButton testid="ca-hashtags" loading={pending === "hashtags"} label="Hashtags" onClick={() => run("hashtags")} />
            <AIButton testid="ca-talking-points" loading={pending === "talking_points"} label="Talking points" onClick={() => run("talking_points")} />
          </div>

          {ai.result && (
            <AIPanel testid="ca-panel" title="Ideas for you" loading={!!pending}
              onRegenerate={() => run(ai.action)} onDismiss={() => ai.reset()}>
              {ai.action === "content_ideas" && ai.result.items && (
                <div className="space-y-2">
                  {ai.result.items.map((it, i) => (
                    <div key={i} className="rounded-xl border border-emerald-200 bg-white p-3">
                      <p className="text-sm font-semibold text-zinc-800">{it.title}</p>
                      {it.description && <p className="text-xs text-zinc-500">{it.description}</p>}
                    </div>
                  ))}
                </div>
              )}
              {ai.action === "captions" && ai.result.items && (
                <div className="space-y-2">
                  {ai.result.items.map((c, i) => (
                    <div key={i} className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-white p-3">
                      <p className="flex-1 whitespace-pre-line text-sm text-zinc-700">{c}</p>
                      <CopyButton testid={`ca-caption-copy-${i}`} text={c} />
                    </div>
                  ))}
                </div>
              )}
              {ai.action === "talking_points" && ai.result.items && (
                <ul className="space-y-1.5">
                  {ai.result.items.map((t, i) => (
                    <li key={i} className="flex gap-2 text-sm text-zinc-700"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" /> {t}</li>
                  ))}
                </ul>
              )}
              {ai.action === "hashtags" && ai.result.items && (
                <div>
                  <div className="flex flex-wrap gap-1.5">
                    {ai.result.items.map((h, i) => (
                      <span key={i} className="rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-xs text-emerald-700">{h}</span>
                    ))}
                  </div>
                  <div className="mt-2"><CopyButton testid="ca-hash-copy" text={ai.result.items.join(" ")} label="Copy all" /></div>
                </div>
              )}
            </AIPanel>
          )}
        </>
      )}
    </div>
  );
}
